/**
 * Holiday Sale hero: drop status in the sale's time zone — a countdown to the
 * next daily drop and "Day [current] of [total]" with its progress bar, both
 * calculated from the first/last drop dates and the drop hour.
 * "[count] bundles live" is filled in from the calendar section's
 * `holiday-sale:live-count` event.
 * The anchor buttons need no code here: theme.liquid smooth-scrolls every
 * a[href^="#"] link with a sticky-header offset.
 */
(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const formatters = {};

  // Wall-clock date/time parts of a timestamp in an IANA time zone
  function zonedParts(timestamp, timeZone) {
    if (!formatters[timeZone]) {
      formatters[timeZone] = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });
    }

    const parts = {};
    formatters[timeZone].formatToParts(timestamp).forEach(({ type, value }) => {
      parts[type] = Number(value);
    });
    return parts;
  }

  // UTC timestamp of hour:00 on a calendar date in an IANA time zone
  function zonedTime(date, hour, timeZone) {
    const guess = Date.UTC(date.year, date.month - 1, date.day, hour);
    const p = zonedParts(guess, timeZone);
    const offset = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - guess;
    return guess - offset;
  }

  // { year, month, day } from YYYY-MM-DD, or null
  function parseDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  }

  // Whole calendar days from date a to date b
  function daysBetween(a, b) {
    return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY_MS);
  }

  class HolidaySaleDrops extends HTMLElement {
    connectedCallback() {
      const dropHour = Number(this.dataset.dropHour);
      this.timeZone = this.dataset.timeZone || 'America/New_York';
      this.dropHour = Number.isFinite(dropHour) ? dropHour : 9;
      this.firstDate = parseDate(this.dataset.firstDrop);
      this.lastDate = parseDate(this.dataset.lastDrop);
      this.lastDrop = this.lastDate ? zonedTime(this.lastDate, this.dropHour, this.timeZone) : null;
      this.totalDays =
        this.firstDate && this.lastDate ? Math.max(0, daysBetween(this.firstDate, this.lastDate) + 1) : 0;

      this.timerCard = this.querySelector('[data-timer-card]');
      this.hoursEl = this.querySelector('[data-countdown-hours]');
      this.minutesEl = this.querySelector('[data-countdown-minutes]');
      this.secondsEl = this.querySelector('[data-countdown-seconds]');
      this.textEl = this.querySelector('[data-countdown-text]');
      this.endedEl = this.querySelector('[data-countdown-ended]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.progressTrack = this.querySelector('[data-progress-track]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.current = null;

      // "[count] bundles live": the Holiday Sale calendar section on the page counts them
      this.liveCountEl = this.querySelector('[data-live-count]');
      if (this.liveCountEl) {
        this.onLiveCount = (event) => this.renderLiveCount(event.detail.count);
        document.addEventListener('holiday-sale:live-count', this.onLiveCount);
        const calendar = document.querySelector('holiday-sale-calendar');
        if (calendar && Number.isFinite(calendar.liveCount)) this.renderLiveCount(calendar.liveCount);
      }

      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onLiveCount) document.removeEventListener('holiday-sale:live-count', this.onLiveCount);
    }

    renderLiveCount(count) {
      this.liveCountEl.textContent = this.liveCountEl.dataset.template.split('[count]').join(count);
      this.liveCountEl.classList.remove('is-pending');
    }

    // Next drop_hour:00 after `now` (today's or tomorrow's), null after the last drop
    nextDrop(now) {
      const today = zonedParts(now, this.timeZone);
      let drop = zonedTime(today, this.dropHour, this.timeZone);
      if (drop <= now) {
        const tomorrow = new Date(Date.UTC(today.year, today.month - 1, today.day + 1));
        drop = zonedTime(
          { year: tomorrow.getUTCFullYear(), month: tomorrow.getUTCMonth() + 1, day: tomorrow.getUTCDate() },
          this.dropHour,
          this.timeZone
        );
      }

      if (this.lastDrop && drop > this.lastDrop) return null;
      return drop;
    }

    // Drops that have unlocked by `now`: 0 before the first one, totalDays after the last one
    dropsSoFar(now) {
      const today = zonedParts(now, this.timeZone);
      let drops = daysBetween(this.firstDate, today);
      if (zonedTime(today, this.dropHour, this.timeZone) <= now) drops += 1;
      return Math.min(Math.max(drops, 0), this.totalDays);
    }

    renderProgress(now) {
      if (!this.totalDays) {
        if (this.progressTrack) this.progressTrack.hidden = true;
        return;
      }

      const current = this.dropsSoFar(now);
      if (current === this.current) return;
      this.current = current;

      if (this.progressLabel) {
        this.progressLabel.textContent = this.progressLabel.dataset.template
          .split('[current]')
          .join(current)
          .split('[total]')
          .join(this.totalDays);
      }
      if (this.progressFill) {
        this.progressFill.style.width = `${(current / this.totalDays) * 100}%`;
      }
    }

    tick() {
      const now = Date.now();
      this.renderProgress(now);

      const drop = this.nextDrop(now);
      if (drop === null) {
        this.dataset.state = 'ended';
        if (!this.endedEl && this.timerCard) this.timerCard.hidden = true;
        return;
      }

      const totalSeconds = Math.floor(Math.max(0, drop - now) / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.hoursEl.textContent = String(hours).padStart(2, '0');
      this.minutesEl.textContent = String(minutes).padStart(2, '0');
      this.secondsEl.textContent = String(seconds).padStart(2, '0');

      // Screen reader text changes once a minute, not every second
      const text = `${hours} ${this.dataset.hoursLabel} ${minutes} ${this.dataset.minutesLabel}`.toLowerCase();
      if (this.textEl.textContent !== text) this.textEl.textContent = text;

      this.dataset.state = 'running';
      this.timer = setTimeout(() => this.tick(), 1000 - (now % 1000) + 20);
    }
  }

  if (!customElements.get('holiday-sale-drops')) {
    customElements.define('holiday-sale-drops', HolidaySaleDrops);
  }
})();
